import classnames from 'classnames/bind'
import { ChangeEvent, useEffect } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalBreakpoints } from '~/@types/breakpoints'
import { GlobalThemeColors } from '~/@types/colors'
import { Variant } from '~/@types/product'

import BlurredCta from '~/components/UI/BlurredCta'
import MaxCountTag from '~/components/UI/MaxCountTag'
import { ProductHeaderProps } from '~/components/UI/ProductHeader'
import { MultiBundleSelector } from '~/components/UI/ProductHeader/MultiBundleSelector'
import { ByCharlotVariantSelector } from '~/components/UI/ProductVariantSelector/ByCharlotVariantSelector'
import { PackProductSelector } from '~/components/UI/ProductVariantSelector/PackProductSelector'

import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import useBreakpoint from '~/hooks/useBreakpoint'
import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'

import { getVariantFromSelectedOptions } from '~/utils/variant-helpers'

import { CTA, FORM } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface ProductVariantSelectorProps {
  className?: string

  isGiftCard?: ProductHeaderProps['isGiftCard']
  showImageVariants?: boolean

  setByCharlotMobileOpen?(state: boolean): void
}

export const customTextVariant = 'Légende'
export const atelierDateVariant = 'Date'

export interface CustomTextAreaProps {
  className?: string
  value?: string
  maxLength?: number
  style?: string
  asDate?: boolean
  onChange?: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void
}

export function CustomTextArea({
  className,
  value,
  maxLength,
  style,
  asDate,
  onChange,
}: CustomTextAreaProps) {
  const t = useTranslate()
  return (
    <div className={cx(css.CustomTextArea, className)}>
      {asDate ? (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e)}
          className={cx(css.dateInput, style)}
        />
      ) : (
        <>
          <textarea
            value={value}
            maxLength={maxLength}
            onChange={(e) => {
              onChange(e)
            }}
            placeholder={t(FORM.TEXTAREA_PLACEHOLDER)}
            className={cx(css.textareaInput, style)}
          />
          {maxLength && (
            <MaxCountTag
              className={cx(css.maxLengthInfo)}
              maxLength={maxLength}
              currentText={value}
            />
          )}
        </>
      )}
    </div>
  )
}

function ProductVariantSelector({
  className,
  isGiftCard,
  showImageVariants,
  setByCharlotMobileOpen,
}: ProductVariantSelectorProps) {
  const { setAtelierDate, isMultiBundle } = useProductHeaderProvider()

  const { setUserSelection } = useUserSelectionProvider()

  const t = useTranslate()
  const {
    activeTabAvailableProducts,
    activeTabIndex,
    variantId,
    variants,
    currentVariant,
    itemsToSelect,
  } = useGetStateInformationsFromUserSelection()

  const options =
    variants?.reduce((acc, variant) => {
      variant?.selectedOptions?.forEach((option) => {
        if (!acc[option.name]) {
          acc[option.name] = []
        }
        const selectedOptions = [
          ...(currentVariant?.selectedOptions?.filter?.(
            (selectedOption) => selectedOption.name !== option.name,
          ) ?? []),
          option,
        ]
        const variant = getVariantFromSelectedOptions(selectedOptions, variants)

        if (
          variant &&
          !acc[option.name].find((v: Variant) => v.id === variant.id)
        ) {
          acc[option.name].push({
            ...variant,
            title: option.value,
          })
        }
      })
      return acc
    }, {}) ?? {}

  const atelierDatesVariants = options[atelierDateVariant]

  const isMobile = useBreakpoint(GlobalBreakpoints.L)

  useEffect(() => {
    if (itemsToSelect === 1) return
    if (!isMobile) {
      setByCharlotMobileOpen(false)
    } else {
      setUserSelection((prev) =>
        prev.map((tab) => ({ ...tab, isActive: false })),
      )
    }
  }, [isMobile])

  const handleSelectorClick = () => {
    isMobile ? setByCharlotMobileOpen(true) : null
  }

  useEffect(() => {
    if (atelierDatesVariants) {
      const value = atelierDatesVariants?.find(
        (variant) => variant.id === variantId,
      )?.title
      setAtelierDate(value)
    }
  }, [atelierDatesVariants, variantId])

  return (
    <div className={cx(css.ProductVariantSelector, className)}>
      <div className={cx(css.byCharlotSelector)}>
        {Array(itemsToSelect)
          .fill(null)
          ?.map((_, i) => (
            <div
              key={`selector_${i}`}
              className={cx(css.selectorHeader, {
                active: i === activeTabIndex,
                [css.isLast]: i === itemsToSelect - 1,
              })}>
              {isMultiBundle && (
                <MultiBundleSelector
                  index={i}
                  className={cx(css.multiBundleSelector)}
                  clickFunction={handleSelectorClick}
                />
              )}
              {activeTabIndex === i && (!isMobile || !isMultiBundle) && (
                <>
                  {isMultiBundle && (
                    <PackProductSelector
                      className={cx(
                        css.packProductSelector,
                        css.hideOnSmallScreen,
                      )}
                    />
                  )}
                  <ByCharlotVariantSelector
                    className={cx(css.variantSelector)}
                    isGiftCard={isGiftCard}
                    showImageVariants={showImageVariants}
                  />
                  {isMultiBundle && (
                    <div className={cx(css.nextCtaContainer)}>
                      <BlurredCta
                        className={cx(css.nextCta)}
                        onClick={() => {
                          setUserSelection((prev) => {
                            const nextActive =
                              activeTabIndex + 1 < prev.length
                                ? activeTabIndex + 1
                                : -1
                            const newState = prev.map((tab, index) => ({
                              ...tab,
                              isActive: index === nextActive,
                            }))
                            return newState
                          })
                        }}
                        theme={GlobalThemeColors.Black}>
                        {t(CTA.NEXT)}
                      </BlurredCta>
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
      </div>
    </div>
  )
}

ProductVariantSelector.defaultProps = {}

export default ProductVariantSelector
